import { Injectable } from '@angular/core';
import { AppSettingsService, ITicketConfiguration } from './app-settings.service';
import { Observable, ReplaySubject, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService as XSightHttpService } from './http.service';
import { IUserInfoModel } from './helpdesk.typings';
import { HttpHeaders, HttpParams } from '@angular/common/http';

export enum LicenseDetailsStatus {
  /// <summary>License has not yet been activated</summary>
  inactive = 0,

  /// <summary>License was activated and has expired</summary>
  expired = 1,

  /// <summary>License is active and valid</summary>
  active = 2,

  /// <summary>License is invalid</summary>
  invalid = 3,
}

export interface ILicenceDetails {
  /* Indicates the licence start date */
  licenseStartDate: string;
  /* Indicates the licence end date */
  licenseEndDate: string;
  /* Indicates the licence type */
  licenseType: string;
  /* Indicates the number of users of the licence */
  numberOfUsers: number;
  /* Indicates the licence signature */
  licenseSignature: string;
  /** Indicates the current status of the licence */
  status: string;
  /* Indicates the licence total duration in days */
  $totalLengthInDays: number;
  /* Indicates the licence number of days used */
  $daysUsed: number;
  /* Indicates the licence number of days remaining */
  $daysRemaining: number;
  /* Contain error message */
  errorMessage: string;
  /* Contain error status */
  errorStatus: LicenseDetailError;
 /* Indicates the licence id */
  xsightInstallationId: string;
  /* The mc registration code */
  mcRegistrationCode: string;
}

export const enum LicenseDetailError {
  NoError = 0,
  NotFound = 1,
  UnprocessableEntity = 2,
  Forbidden = 3,
  OtherHttpError = 4,
  UnknownError = 5,
  LicenseNotActive = 6,
}

export interface IMCGroupConfiguration {
  groupId?: number;
  administratorGroup?: string;
  technicianGroup?: string;
  systemAdministratorGroup?: string;
}

export interface IAccountServiceConfiguration {
  systemAdministratorGroup?: string;
}

export interface ISotiAssistUri {
  uri?: string;
}

export interface IMcServiceCredential {
  userName?: string;
  updatePassword: boolean;
  userPassword?: string;
}

export interface IMcServiceCredentialValidation {
  /** The MC Service Credential validation result */
  result: boolean;

  /** The MC Service Credential validation result message */
  message?: string;
}

export interface NativeLogOnOverrideConfig {
  domains: string;
}

export interface NativeLogOnConfig {
  enabled: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class AccountService {
  licenseDetails: ILicenceDetails;
  constructor(private http: XSightHttpService, private appSettings: AppSettingsService) {}

  public getLicenceDetails = (forceFetch?: boolean): Observable<ILicenceDetails> => {
    //check if license details is over 48 hours
    if (
      !forceFetch &&
      this.licenseDetails &&
      (new Date(this.licenseDetails?.licenseEndDate).getTime() - new Date().getTime()) / 3600000 > 48
    ) {
      return of(this.licenseDetails);
    }
    return this.http.get<ILicenceDetails>(this.appSettings.accountServiceUrl + 'config/license').pipe(
      map(ld => {
        if ((new Date(ld?.licenseEndDate).getTime() - new Date().getTime()) / 3600000 > 48) {
          this.licenseDetails = ld;
        }
        return ld;
      })
    );
  };

  public getLicenceDetailsStatus = (connectionId?: number): Observable<LicenseDetailsStatus> => {
    let params: HttpParams;
    if (connectionId != null || connectionId != undefined) {
      params = new HttpParams().set('connectionId', connectionId);
    }
    return this.http.get<LicenseDetailsStatus>(this.appSettings.accountServiceUrl + 'config/license/status', {
      params,
    });
  };

  public updateLicenceDetails = (updatedLicenceDetails: ILicenceDetails): void => {
    this.licenseDetails = updatedLicenceDetails;
  };

  public getMcGroupConfig = (): Observable<IMCGroupConfiguration> => {
    return this.http.get<IMCGroupConfiguration>(this.appSettings.accountServiceUrl + 'config/mcgroup');
  };

  public updateMcGroupConfig = (objMcgroup: IMCGroupConfiguration): Observable<IMCGroupConfiguration> => {
    return this.http.put<IMCGroupConfiguration>(this.appSettings.accountServiceUrl + 'config/mcgroup', objMcgroup);
  };

  public getAccountServiceConfig = (): Observable<IAccountServiceConfiguration> => {
    return this.http.get<IAccountServiceConfiguration>(
      this.appSettings.accountServiceUrl + 'config/accountServiceConfig'
    );
  };

  public updateAccountServiceConfig = (
    accountServiceConfig: IAccountServiceConfiguration
  ): Observable<IAccountServiceConfiguration> => {
    return this.http.put<IAccountServiceConfiguration>(
      this.appSettings.accountServiceUrl + 'config/accountServiceConfig',
      accountServiceConfig
    );
  };

  public getSotiXSightUri = (): Observable<ISotiAssistUri> => {
    return this.http.get<ISotiAssistUri>(this.appSettings.accountServiceUrl + 'sotixsighturi');
  };

  public getServiceCredentialConfig = (): Observable<IMcServiceCredential> => {
    return this.http.get<IMcServiceCredential>(this.appSettings.accountServiceUrl + 'config/mcServiceCredential');
  };

  public getDomains = (): Observable<string> => {
    return this.http
      .get<NativeLogOnOverrideConfig>(this.appSettings.accountServiceUrl + 'config/nativeLogOnOverride')
      .pipe(map(n => n.domains));
  };

  public getEnableNativeLogin = (): Observable<boolean> => {
    return this.http
      .get<NativeLogOnConfig>(this.appSettings.accountServiceUrl + 'config/nativeLogOn')
      .pipe(map(n => n.enabled));
  };

  public updateEnableNativeLogin = (value: boolean): Observable<NativeLogOnConfig> => {
    const data = { enabled: value } as NativeLogOnConfig;
    return this.http.put<NativeLogOnConfig>(this.appSettings.accountServiceUrl + 'config/nativeLogOn', data);
  };

  public updateDomains = (value: string): Observable<NativeLogOnOverrideConfig> => {
    const data = { domains: value } as NativeLogOnOverrideConfig;
    return this.http.put<NativeLogOnOverrideConfig>(
      this.appSettings.accountServiceUrl + 'config/nativeLogOnOverride',
      data
    );
  };

  public updateCredentials = (data: IMcServiceCredential): Observable<IMcServiceCredential> => {
    return this.http.put<IMcServiceCredential>(this.appSettings.accountServiceUrl + 'config/mcServiceCredential', data);
  };

  public validateCredentials = (): Observable<IMcServiceCredentialValidation> => {
    return this.http.get<IMcServiceCredentialValidation>(
      this.appSettings.accountServiceUrl + 'config/mcServiceCredential/validate'
    );
  };

  public forceRefreshLicense = (id?: number | null): Observable<ILicenceDetails> => {
    const params = id !== undefined ? { id: id.toString(), forceRefresh: 'true' } : { forceRefresh: 'true' };
    return this.http.get<ILicenceDetails>(this.appSettings.accountServiceUrl + 'config/license', {
      params,
    });
  };

  public activateTrialLicence = (): Observable<ILicenceDetails> => {
    return this.http.put<ILicenceDetails>(this.appSettings.accountServiceUrl + 'config/license', {});
  };

  public getAssigneeList = (searchText: string): Observable<IUserInfoModel[]> => {
    let headers = new HttpHeaders();
    const options = {
      headers: headers,
      params: { searchText: searchText },
    };
    return this.http.get<any>(this.appSettings.accountServiceUrl + 'assigneelist', options);
  };

  /** Update the ticket configuration */
  public updateTicketConfiguration = (value: ITicketConfiguration): Observable<ITicketConfiguration> => {
    return this.http.put<ITicketConfiguration>(
      this.appSettings.accountServiceUrl + `config/ticketConfiguration`,
      value
    );
  };
}
